import type { FC } from "react"
import { useTranslation } from "react-i18next"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"

import { getNumericFormatter } from "helpers/formatters/plots"
import SeverityDot from "widgets/plots/SeverityDot"
import type { HeatmapRow, SpanInfo } from "./helpers"

interface SummaryRowProps {
  rows: HeatmapRow[]
  // Aligns the totals with the lanes, which start after the method gutter.
  labelWidth: number
  laneErrors?: Partial<Record<"aar" | "dlr", boolean>>
  // The window the counts cover, preformatted.
  dateRangeLabel?: string
  // The span under the pointer, or the selected one once hover ends.
  activeInfo: SpanInfo | null
  // Already built copy, null when this span is nobody's last limiter.
  lastLimiterNote: string | null
  colorFor: (intensity: number) => string
  locale: string
}

/**
 * The strip above the lanes: per-method event totals with the window they were
 * counted over, and the read-out for the span currently under the pointer.
 */
const SummaryRow: FC<SummaryRowProps> = ({
  rows,
  labelWidth,
  laneErrors,
  dateRangeLabel,
  activeInfo,
  lastLimiterNote,
  colorFor,
  locale,
}) => {
  const { t } = useTranslation()
  // The counts next to it are localized, so the percentage has to be too.
  const formatPct = getNumericFormatter(locale, 1)

  return (
    /* One row on wide screens. Narrower there isn't room for both, so the
       readout moves above the totals — `column-reverse` puts it first even
       though it's the second child. */
    <Stack
      direction={{ xs: "column-reverse", lg: "row-reverse" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", lg: "center" }}
      spacing={{ xs: 0.25, lg: 0 }}
      sx={{ my: 1, ml: `${labelWidth}px` }}
    >
      {/* Totals plus the window they were counted over, on the right since
          row-reverse puts the readout left. Wraps rather than squeezes. */}
      <Stack
        direction="row"
        spacing={2.5}
        useFlexGap
        flexWrap="wrap"
        alignItems="baseline"
      >
        {rows.map((row) => {
          const isDlr = row.dataKey === "dlr"
          // A failed method has no total to print: its rows never landed, so
          // `0` here would read as "this method never limited the line".
          // Says what the lane and the card already say instead.
          const hasLaneError = Boolean(laneErrors?.[isDlr ? "dlr" : "aar"])

          return (
            <Typography
              key={row.dataKey}
              variant="caption"
              sx={{
                color: "text.primary",
                fontSize: "0.65rem",
                letterSpacing: "0.02em",
              }}
            >
              {t(isDlr ? "spans.ampacity.events.dlr" : "spans.ampacity.events.aar")}:{" "}
              {hasLaneError ? (
                <Box component="span" sx={{ color: "text.disabled" }}>
                  {t("spans.ampacity.lane.unavailableShort")}
                </Box>
              ) : (
                <Box component="span" sx={{ color: "text.primary", fontWeight: 700 }}>
                  {row.totalCount.toLocaleString(locale)}
                </Box>
              )}
            </Typography>
          )
        })}
        {dateRangeLabel && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.65rem",
              letterSpacing: "0.02em",
              whiteSpace: "nowrap",
            }}
          >
            {dateRangeLabel}
          </Typography>
        )}
      </Stack>
      <Typography
        variant="caption"
        sx={{
          minHeight: "1rem",
          fontSize: "0.65rem",
          color: "text.secondary",
          maxWidth: "100%",
          minWidth: 0,
          whiteSpace: "normal",
        }}
      >
        {activeInfo ? (
          <>
            {activeInfo.spanId != null
              ? t("spans.ampacity.hover.spanWithId", {
                  index: activeInfo.spanIndex,
                  id: activeInfo.spanId,
                })
              : t("spans.ampacity.hover.span", { index: activeInfo.spanIndex })}
            {activeInfo.values.map((v) => (
              <Box
                component="span"
                key={v.dataKey}
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, ml: 1 }}
              >
                <Box component="span" sx={{ opacity: 0.5, mr: 0.5 }}>
                  &middot;
                </Box>
                <SeverityDot color={colorFor(v.intensity)} size={7} />
                <Box component="span">
                  {v.dataKey.toUpperCase()}{" "}
                  {laneErrors?.[v.dataKey === "dlr" ? "dlr" : "aar"] ? (
                    <Box component="span" sx={{ color: "text.disabled" }}>
                      {t("spans.ampacity.lane.unavailableShort")}
                    </Box>
                  ) : (
                    t("spans.ampacity.hover.pctCount", {
                      pct: formatPct(v.pct),
                      value: v.count.toLocaleString(locale),
                    })
                  )}
                </Box>
              </Box>
            ))}
            {lastLimiterNote && (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  ml: 1,
                  color: "text.secondary",
                }}
              >
                <Box component="span" sx={{ opacity: 0.5, mr: 0.5 }}>
                  &middot;
                </Box>
                {lastLimiterNote}
              </Box>
            )}
          </>
        ) : (
          " "
        )}
      </Typography>
    </Stack>
  )
}

export default SummaryRow
